import { lazy } from 'react';
import { Navigate } from 'react-router-dom';

// project imports
import MainLayout from 'layout/MainLayout';
import ErrorBoundary from './ErrorBoundary';
import Loadable from 'ui-component/Loadable';
import AuthGuard from 'utils/route-guard/AuthGuard';
import { ECHOS_STUDIES_BASE_PATH } from 'utils/sectionPaths';

// dashboard routing
const DashboardDefault = Loadable(lazy(() => import('views/dashboard/Default')));
const DashboardAnalytics = Loadable(lazy(() => import('views/dashboard/Analytics')));

// widget routing
const WidgetStatistics = Loadable(lazy(() => import('views/widget/Statistics')));
const WidgetData = Loadable(lazy(() => import('views/widget/Data')));
const WidgetChart = Loadable(lazy(() => import('views/widget/Chart')));

// forms component routing
const FrmComponentsTextfield = Loadable(lazy(() => import('views/forms/components/TextField')));
const FrmComponentsButton = Loadable(lazy(() => import('views/forms/components/Button')));
const FrmComponentsCheckbox = Loadable(lazy(() => import('views/forms/components/Checkbox')));
const FrmComponentsRadio = Loadable(lazy(() => import('views/forms/components/Radio')));
const FrmComponentsSwitch = Loadable(lazy(() => import('views/forms/components/Switch')));
const FrmComponentsAutoComplete = Loadable(lazy(() => import('views/forms/components/AutoComplete')));
const FrmComponentsSlider = Loadable(lazy(() => import('views/forms/components/Slider')));
const FrmComponentsDateTime = Loadable(lazy(() => import('views/forms/components/DateTime')));

// forms plugins layout
const FrmLayoutLayout = Loadable(lazy(() => import('views/forms/layouts/Layouts')));
const FrmLayoutMultiColumnForms = Loadable(lazy(() => import('views/forms/layouts/MultiColumnForms')));
const FrmLayoutActionBar = Loadable(lazy(() => import('views/forms/layouts/ActionBar')));
const FrmLayoutStickyActionBar = Loadable(lazy(() => import('views/forms/layouts/StickyActionBar')));

// forms plugins routing
const FrmAutocomplete = Loadable(lazy(() => import('views/forms/plugins/AutoComplete')));
const FrmMask = Loadable(lazy(() => import('views/forms/plugins/Mask')));
const FrmClipboard = Loadable(lazy(() => import('views/forms/plugins/Clipboard')));
const FrmRecaptcha = Loadable(lazy(() => import('views/forms/plugins/Recaptcha')));
const FrmModal = Loadable(lazy(() => import('views/forms/plugins/Modal')));
const FrmTooltip = Loadable(lazy(() => import('views/forms/plugins/Tooltip')));
const FrmDropzone = Loadable(lazy(() => import('views/forms/plugins/Dropzone')));

// forms validation
const FrmFormsValidation = Loadable(lazy(() => import('views/forms/forms-validation')));
const FrmFormsWizard = Loadable(lazy(() => import('views/forms/forms-wizard')));

// chart routing
const ChartApexchart = Loadable(lazy(() => import('views/forms/chart/Apexchart')));

// basic ui-elements routing
const BasicUIAccordion = Loadable(lazy(() => import('views/ui-elements/basic/UIAccordion')));
const BasicUIAvatar = Loadable(lazy(() => import('views/ui-elements/basic/UIAvatar')));
const BasicUIBadges = Loadable(lazy(() => import('views/ui-elements/basic/UIBadges')));
const BasicUIBreadcrumb = Loadable(lazy(() => import('views/ui-elements/basic/UIBreadcrumb')));
const BasicUICards = Loadable(lazy(() => import('views/ui-elements/basic/UICards')));
const BasicUIChip = Loadable(lazy(() => import('views/ui-elements/basic/UIChip')));
const BasicUIList = Loadable(lazy(() => import('views/ui-elements/basic/UIList')));
const BasicUITabs = Loadable(lazy(() => import('views/ui-elements/basic/UITabs')));

// advance ui-elements routing
const AdvanceUIAlert = Loadable(lazy(() => import('views/ui-elements/advance/UIAlert')));
const AdvanceUIDialog = Loadable(lazy(() => import('views/ui-elements/advance/UIDialog')));
const AdvanceUIPagination = Loadable(lazy(() => import('views/ui-elements/advance/UIPagination')));
const AdvanceUIProgress = Loadable(lazy(() => import('views/ui-elements/advance/UIProgress')));
const AdvanceUIRating = Loadable(lazy(() => import('views/ui-elements/advance/UIRating')));
const AdvanceUISnackbar = Loadable(lazy(() => import('views/ui-elements/advance/UISnackbar')));
const AdvanceUISkeleton = Loadable(lazy(() => import('views/ui-elements/advance/UISkeleton')));
const AdvanceUISpeeddial = Loadable(lazy(() => import('views/ui-elements/advance/UISpeeddial')));
const AdvanceUITimeline = Loadable(lazy(() => import('views/ui-elements/advance/UITimeline')));
const AdvanceUIToggleButton = Loadable(lazy(() => import('views/ui-elements/advance/UIToggleButton')));
const AdvanceUITreeview = Loadable(lazy(() => import('views/ui-elements/advance/UITreeview')));

// pricing page routing
const PagesPrice1 = Loadable(lazy(() => import('views/pages/pricing/Price1')));
const PagesPrice2 = Loadable(lazy(() => import('views/pages/pricing/Price2')));

// utilities routing
const UtilsTypography = Loadable(lazy(() => import('views/utilities/Typography')));
const UtilsColor = Loadable(lazy(() => import('views/utilities/Color')));
const UtilsShadow = Loadable(lazy(() => import('views/utilities/Shadow')));
const UtilsAnimation = Loadable(lazy(() => import('views/utilities/Animation')));
const UtilsGrid = Loadable(lazy(() => import('views/utilities/Grid')));

// sample page routing
const SamplePage = Loadable(lazy(() => import('views/sample-page')));

//teleradnow - administrative tools
const AdministrativeToolsIndexPage = Loadable(lazy(() => import('pages/administrativeTools/AdministrativeToolsIndexPage')));
const LocationsPage = Loadable(lazy(() => import('pages/administrativeTools/locations')));
const LocationFormPage = Loadable(lazy(() => import('pages/administrativeTools/locations/LocationFormPage')));
const UsersPage = Loadable(lazy(() => import('pages/administrativeTools/users')));
const UserFormPage = Loadable(lazy(() => import('pages/administrativeTools/users/UserFormPage')));
const DoctorsPage = Loadable(lazy(() => import('pages/administrativeTools/doctors')));
const DoctorFormPage = Loadable(lazy(() => import('pages/administrativeTools/doctors/DoctorFormPage')));
const StatisticsPage = Loadable(lazy(() => import('pages/administrativeTools/statistics')));
const SearchRecordsPage = Loadable(lazy(() => import('pages/administrativeTools/search')));
const ContactsPage = Loadable(lazy(() => import('pages/administrativeTools/contacts')));

//teleradnow - cardiac studies
const NuclearStressWorkspacePage = Loadable(lazy(() => import('modules/cardiac/nuclear-stress-test/pages/NuclearStressWorkspacePage')));
const MugaWorkspacePage = Loadable(lazy(() => import('modules/cardiac/muga/pages/MugaWorkspacePage')));
const StressTestExerciseWorkspacePage = Loadable(lazy(() => import('modules/cardiac/stress-test-exercise/pages/StressTestExerciseWorkspacePage')));

//teleradnow - echos studies
const EchoStudiesIndexPage = Loadable(lazy(() => import('modules/echos/shared/EchoStudiesIndexPage')));
const EchoStudyRoutePage   = Loadable(lazy(() => import('modules/echos/shared/EchoStudyRoutePage')));
const RenalScanWorkspacePage = Loadable(lazy(() => import('modules/nuclear/renal-scan/RenalScanWorkspacePage')));
const GeneralNuclearWorkspacePage = Loadable(lazy(() => import('modules/nuclear/general-nuclear/GeneralNuclearWorkspacePage')));
const ThyroidScanWorkspacePage = Loadable(lazy(() => import('modules/nuclear/thyroid-scan/ThyroidScanWorkspacePage')));
const LungScanWorkspacePage = Loadable(lazy(() => import('modules/nuclear/lung-scan/LungScanWorkspacePage')));
const Iodine123ThyroidScanWorkspacePage = Loadable(lazy(() => import('modules/nuclear/iodine123-thyroid-scan/Iodine123ThyroidScanWorkspacePage')));

const LegacyModulePlaceholderPage = Loadable(lazy(() => import('pages/LegacyModulePlaceholderPage')));

// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes = {
  path: '/',
  element: (
    <AuthGuard>
      <MainLayout />
    </AuthGuard>
  ),
  children: [
    {
      path: '/administrative-tools',
      children: [
        { index: true, element: <AdministrativeToolsIndexPage /> },
        { path: 'locations/new', element: <Navigate to="/administrative-tools/locations?tab=new" replace /> },
        { path: 'locations/list', element: <Navigate to="/administrative-tools/locations?tab=list" replace /> },
        { path: 'locations/:locCode', element: <LocationFormPage /> },
        { path: 'users/new', element: <Navigate to="/administrative-tools/users?tab=new" replace /> },
        { path: 'users/list', element: <Navigate to="/administrative-tools/users?tab=list" replace /> },
        { path: 'users/:useCode', element: <UserFormPage /> },
        { path: 'doctors/list', element: <Navigate to="/administrative-tools/doctors?tab=list" replace /> },
        { path: 'doctors/new', element: <Navigate to="/administrative-tools/doctors?tab=new" replace /> },
        { path: 'doctors/:docCode', element: <DoctorFormPage /> },
        { path: 'doctors', element: <DoctorsPage /> },
        { path: 'statistics', element: <StatisticsPage /> },
        { path: 'search', element: <SearchRecordsPage /> },
        { path: 'contacts', element: <ContactsPage /> },
        { path: 'contacts/list', element: <ContactsPage /> },
        { path: 'user/new', element: <Navigate to="/administrative-tools/users?tab=new" replace /> },
        { path: 'user', element: <Navigate to="/administrative-tools/users" replace /> },
        { path: 'location/new', element: <Navigate to="/administrative-tools/locations?tab=new" replace /> },
        { path: 'location', element: <Navigate to="/administrative-tools/locations" replace /> },
        { path: 'doctor/new', element: <Navigate to="/administrative-tools/doctors?tab=new" replace /> },
        { path: 'locations', element: <LocationsPage /> },
        { path: 'users', element: <UsersPage /> }
      ]
    },
    {
      path: '/cardiac_studies',
      children: [
        { path: 'nuclear_stress_test', element: <NuclearStressWorkspacePage /> },
        { path: 'muga', element: <MugaWorkspacePage /> },
        { path: 'stress_test_(exercise_only)', element: <StressTestExerciseWorkspacePage /> }
      ]
    },
    {
      path: ECHOS_STUDIES_BASE_PATH,
      children: [
        { index: true, element: <EchoStudiesIndexPage /> },
        { path: ':echoStudyId', element: <EchoStudyRoutePage /> }
      ]
    },
    {
      path: '/nuclear_studies',
      children: [
        { path: 'renal_scan', element: <RenalScanWorkspacePage /> },
        {
          path: 'general',
          children: [
            { index: true, element: <GeneralNuclearWorkspacePage /> },
            { path: 'create_report', element: <Navigate to="/nuclear_studies/general?view=create_report" replace /> },
            { path: 'view_pending',  element: <Navigate to="/nuclear_studies/general?view=view_pending"  replace /> },
            { path: 'view_final',    element: <Navigate to="/nuclear_studies/general?view=view_final"    replace /> },
          ],
        },
        {
          path: 'thyroid_parathyroid_scan',
          children: [
            { index: true, element: <ThyroidScanWorkspacePage /> },
            { path: 'create_report', element: <Navigate to="/nuclear_studies/thyroid_parathyroid_scan?view=create_report" replace /> },
            { path: 'view_pending',  element: <Navigate to="/nuclear_studies/thyroid_parathyroid_scan?view=view_pending"  replace /> },
            { path: 'view_final',    element: <Navigate to="/nuclear_studies/thyroid_parathyroid_scan?view=view_final"    replace /> },
          ],
        },
        {
          path: 'lung_scan',
          children: [
            { index: true, element: <LungScanWorkspacePage /> },
            { path: 'create_report', element: <Navigate to="/nuclear_studies/lung_scan?view=create_report" replace /> },
            { path: 'view_pending',  element: <Navigate to="/nuclear_studies/lung_scan?view=view_pending"  replace /> },
            { path: 'view_final',    element: <Navigate to="/nuclear_studies/lung_scan?view=view_final"    replace /> },
          ],
        },
        {
          path: 'iodine_123_thyroid_scan',
          children: [
            { index: true, element: <Iodine123ThyroidScanWorkspacePage /> },
            { path: 'create_report', element: <Navigate to="/nuclear_studies/iodine_123_thyroid_scan?view=create_report" replace /> },
            { path: 'view_pending',  element: <Navigate to="/nuclear_studies/iodine_123_thyroid_scan?view=view_pending"  replace /> },
            { path: 'view_final',    element: <Navigate to="/nuclear_studies/iodine_123_thyroid_scan?view=view_final"    replace /> },
          ],
        },
        { path: '*', element: <LegacyModulePlaceholderPage /> },
      ],
    },
    {
      path: '/thyroid__parathyroid_scan',
      children: [{ path: '*', element: <Navigate to="/nuclear_studies/thyroid_parathyroid_scan" replace /> }],
    },
    {
      path: '/lung_scan',
      children: [{ path: '*', element: <Navigate to="/nuclear_studies/lung_scan" replace /> }],
    },
    {
      path: '/iodine_123_thyroid_scan',
      children: [{ path: '*', element: <Navigate to="/nuclear_studies/iodine_123_thyroid_scan" replace /> }],
    },
    { path: '/widget/statistics', element: <WidgetStatistics /> },
    { path: '/widget/data', element: <WidgetData /> },
    { path: '/widget/chart', element: <WidgetChart /> },

    { path: '/components/text-field', element: <FrmComponentsTextfield /> },
    { path: '/components/button', element: <FrmComponentsButton /> },
    { path: '/components/checkbox', element: <FrmComponentsCheckbox /> },
    { path: '/components/radio', element: <FrmComponentsRadio /> },
    { path: '/components/autocomplete', element: <FrmComponentsAutoComplete /> },
    { path: '/components/slider', element: <FrmComponentsSlider /> },
    { path: '/components/switch', element: <FrmComponentsSwitch /> },
    { path: '/components/date-time', element: <FrmComponentsDateTime /> },

    { path: '/forms/layouts/layouts', element: <FrmLayoutLayout /> },
    { path: '/forms/layouts/multi-column-forms', element: <FrmLayoutMultiColumnForms /> },
    { path: '/forms/layouts/action-bar', element: <FrmLayoutActionBar /> },
    { path: '/forms/layouts/sticky-action-bar', element: <FrmLayoutStickyActionBar /> },

    { path: '/forms/frm-autocomplete', element: <FrmAutocomplete /> },
    { path: '/forms/frm-mask', element: <FrmMask /> },
    { path: '/forms/frm-clipboard', element: <FrmClipboard /> },
    { path: '/forms/frm-recaptcha', element: <FrmRecaptcha /> },
    { path: '/forms/frm-modal', element: <FrmModal /> },
    { path: '/forms/frm-tooltip', element: <FrmTooltip /> },
    { path: '/forms/frm-dropzone', element: <FrmDropzone /> },

    { path: '/forms/forms-validation', element: <FrmFormsValidation /> },
    { path: '/forms/forms-wizard', element: <FrmFormsWizard /> },
    { path: 'forms/charts/apexchart', element: <ChartApexchart /> },

    { path: '/basic/accordion', element: <BasicUIAccordion /> },
    { path: '/basic/avatar', element: <BasicUIAvatar /> },
    { path: '/basic/badges', element: <BasicUIBadges /> },
    { path: '/basic/breadcrumb', element: <BasicUIBreadcrumb /> },
    { path: '/basic/cards', element: <BasicUICards /> },
    { path: '/basic/chip', element: <BasicUIChip /> },
    { path: '/basic/list', element: <BasicUIList /> },
    { path: '/basic/tabs', element: <BasicUITabs /> },

    { path: '/advance/alert', element: <AdvanceUIAlert /> },
    { path: '/advance/dialog', element: <AdvanceUIDialog /> },
    { path: '/advance/pagination', element: <AdvanceUIPagination /> },
    { path: '/advance/progress', element: <AdvanceUIProgress /> },
    { path: '/advance/rating', element: <AdvanceUIRating /> },
    { path: '/advance/snackbar', element: <AdvanceUISnackbar /> },
    { path: '/advance/skeleton', element: <AdvanceUISkeleton /> },
    { path: '/advance/speeddial', element: <AdvanceUISpeeddial /> },
    { path: '/advance/timeline', element: <AdvanceUITimeline /> },
    { path: '/advance/toggle-button', element: <AdvanceUIToggleButton /> },
    { path: '/advance/treeview', element: <AdvanceUITreeview /> },

    { path: '/pages/price/price1', element: <PagesPrice1 /> },
    { path: '/pages/price/price2', element: <PagesPrice2 /> },

    { path: '/utils/util-typography', element: <UtilsTypography /> },
    { path: '/utils/util-color', element: <UtilsColor /> },
    { path: '/utils/util-shadow', element: <UtilsShadow /> },
    { path: '/utils/util-animation', element: <UtilsAnimation /> },
    { path: '/utils/util-grid', element: <UtilsGrid /> },

    { path: '/sample-page', element: <SamplePage /> },
    { path: '/dashboard/default', element: <DashboardDefault /> },
    { path: '/dashboard/analytics', element: <DashboardAnalytics /> },
  ]
};

export default MainRoutes;
